// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import { type FC } from 'react';

import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import Image from '@atlaskit/image/image';

import { Text } from '@atlaskit/primitives/compiled';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightMedia } from '@atlaskit/spotlight/media';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';

import { tourMessages } from './tourMessages';

export type TourCardProps = {
	heading: MessageDescriptor;
	body: MessageDescriptor;
	/** Illustration above the body. Steps with no artwork pass nothing. */
	image?: {
		alt: MessageDescriptor;
		src: string;
	};
	/** 1-based position of this step within the sequence. */
	current: number;
	/** How many steps the sequence has, snapshotted when the tour started. */
	total: number;
	/** Closes the tour without advancing. */
	onDismiss: () => void;
	/** Steps up the sequence. Not called on the final step, which closes the tour instead. */
	onNext: () => void;
	/** Steps back down the sequence. Not called on the first step. */
	onBack: () => void;
	/**
	 * Whether the previous step can still be shown. False when it is the intro and the work item
	 * cannot be closed, since the controller would advance straight off it again.
	 */
	canGoBack: boolean;
	/** The user finished the tour from its final step. */
	onDone: () => void;
};

/**
 * The card chrome shared by every step of the GA changeboarding tour.
 *
 * Which controls appear is keyed off the step's position, not its identity: the arrows step is
 * terminal for a modal user and mid-sequence for a panel user, so only "am I last" can decide
 * between Next and Done.
 */
export const TourCard: FC<TourCardProps> = ({
	body,
	canGoBack,
	current,
	heading,
	image,
	onBack,
	onDismiss,
	onDone,
	onNext,
	total,
}) => {
	const intl = useIntl();

	const isFirst = current <= 1;
	const isLast = current >= total;

	return (
		<SpotlightCard>
			<SpotlightHeader>
				<SpotlightHeadline>
					<FormattedMessage {...heading} />
				</SpotlightHeadline>
				<SpotlightControls>
					<SpotlightDismissControl
						aria-label={intl.formatMessage(tourMessages.dismiss)}
						autoFocus={false}
						onClick={onDismiss}
					/>
				</SpotlightControls>
			</SpotlightHeader>
			{image ? (
				<SpotlightMedia>
					<Image alt={intl.formatMessage(image.alt)} src={image.src} />
				</SpotlightMedia>
			) : null}
			<SpotlightBody>
				<Text>
					<FormattedMessage {...body} />
				</Text>
			</SpotlightBody>
			<SpotlightFooter>
				{/*
				 * A one-step sequence renders no counter: "1 of 1" reads as an error and promises nothing
				 * to come. Product call on the epic's open question for the modal-already-open row.
				 */}
				{total > 1 ? (
					<SpotlightStepCount>
						<FormattedMessage {...tourMessages.stepCount} values={{ current, total }} />
					</SpotlightStepCount>
				) : null}
				<SpotlightActions>
					{isFirst || !canGoBack ? null : (
						<SpotlightSecondaryAction onClick={onBack}>
							<FormattedMessage {...tourMessages.back} />
						</SpotlightSecondaryAction>
					)}
					<SpotlightPrimaryAction onClick={isLast ? onDone : onNext}>
						<FormattedMessage {...(isLast ? tourMessages.done : tourMessages.next)} />
					</SpotlightPrimaryAction>
				</SpotlightActions>
			</SpotlightFooter>
		</SpotlightCard>
	);
};
